import { Component } from '@theme/component';

/**
 * @typedef {object} NetsuiteFormRefs
 * @property {HTMLFormElement} submitForm
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLInputElement} [ipAddress]
 * @property {HTMLInputElement} [honeypot]
 */

import { formatAuPhone } from '@theme/au-phone-format';

/**
 * Tidy-up formatting applied before values reach the CRM, keyed by NetSuite field
 * name. Form builder fields can also opt in with a data-format attribute.
 * @type {Map<string, 'phone' | 'capitalize' | 'uppercase'>}
 */
const FORMAT_BY_NAME = new Map([
  ['phone', 'phone'],
  ['custentityabm_cus_mobilephone', 'phone'],
  ['firstname', 'capitalize'],
  ['lastname', 'capitalize'],
  // Legacy-theme convention: suburb/city is stored UPPERCASE in NetSuite.
  ['city', 'uppercase'],
]);

/**
 * Uppercase the first letter (client-requested), leaving the rest as typed so
 * names like "McDonald" survive.
 * @param {string} raw
 * @returns {string}
 */
function capitalizeFirst(raw) {
  const value = String(raw || '');
  const index = value.search(/\S/);
  if (index === -1) return value;
  return value.slice(0, index) + value[index].toUpperCase() + value.slice(index + 1);
}

/**
 * Apply the field's formatting (data-format, else by NetSuite field name). Only
 * writes when the value actually changes.
 * @param {HTMLInputElement} input
 */
function formatInput(input) {
  const format = input.dataset.format || FORMAT_BY_NAME.get(input.name);
  let value = input.value;

  if (format === 'phone') value = formatAuPhone(value);
  else if (format === 'capitalize') value = capitalizeFirst(value);
  else if (format === 'uppercase') value = value.toUpperCase();

  if (value !== input.value) input.value = value;
}

/**
 * Submits NetSuite CRM forms with a native full-page POST (same approach as the
 * legacy airbag-man theme). NetSuite handles the thank-you redirect from the
 * form configuration in NetSuite.
 *
 * @extends {Component<NetsuiteFormRefs>}
 */
class NetsuiteForm extends Component {
  /** @type {string} */
  #submitLabel = 'Submit';

  /** @type {string} */
  #submittingLabel = 'Submitting…';

  connectedCallback() {
    super.connectedCallback();

    this.#submitLabel = this.dataset.submitLabel || this.#submitLabel;
    this.#submittingLabel = this.dataset.submittingLabel || this.#submittingLabel;

    this.addEventListener('change', this.#formatFieldOnCommit);
    this.#populateIpAddress();

    const country = this.querySelector('select[name="country"]');
    if (country instanceof HTMLSelectElement) this.#syncStateField(country.value);
  }

  /**
   * The state field only exists for Australia (NetSuite accepts exact AU
   * abbreviations, submitted as codes); any other country hides the field
   * and submits no state at all.
   * @param {string} countryValue
   */
  #syncStateField(countryValue) {
    const field = this.querySelector('[data-state-field]');
    const select = this.querySelector('[data-au-state-select]');
    if (!(field instanceof HTMLElement) || !(select instanceof HTMLSelectElement)) return;

    const isAustralia = countryValue === 'AU';
    field.hidden = !isAustralia;
    select.disabled = !isAustralia;
  }

  /**
   * Tidy phone/name fields when committed (blur), per the client's formats.
   * @param {Event} event
   */
  #formatFieldOnCommit = (event) => {
    const input = event.target;

    if (input instanceof HTMLSelectElement && input.name === 'country') {
      this.#syncStateField(input.value);
      return;
    }

    if (input instanceof HTMLInputElement) formatInput(input);
  };

  /** Final formatting pass so the CRM never receives untidied values. */
  #formatAllFields() {
    const form = this.refs.submitForm;
    if (!form) return;

    for (const input of form.querySelectorAll('input')) formatInput(input);

    // Date pickers hold an ISO date (YYYY-MM-DD) and submit nothing themselves; the
    // hidden field they point at carries the date in the account's DD/MM/YYYY.
    for (const picker of form.querySelectorAll('input[data-date-target]')) {
      if (!(picker instanceof HTMLInputElement)) continue;

      const target = document.getElementById(picker.dataset.dateTarget || '');
      if (!(target instanceof HTMLInputElement)) continue;

      const [year, month, day] = picker.value.split('-');
      target.value = year && month && day ? `${day}/${month}/${year}` : '';
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.refs.honeypot?.value) {
      return;
    }

    const form = this.refs.submitForm;

    if (!form || !this.#hasValidAction(form)) {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#setSubmitting(true);
    this.#formatAllFields();
    await this.#populateIpAddress();

    // Native navigation POST — does not re-fire the submit event.
    form.submit();
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  #hasValidAction(form) {
    const action = form.getAttribute('action')?.trim() || this.dataset.actionUrl?.trim();

    return Boolean(action && action.includes('netsuite'));
  }

  async #populateIpAddress() {
    const ipField = this.refs.ipAddress;

    if (!ipField || ipField.value) {
      return;
    }

    try {
      const response = await fetch('https://api.ipify.org?format=json');

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      if (data.ip) {
        ipField.value = data.ip;
      }
    } catch {
      // Leave empty when the IP lookup is unavailable.
    }
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmitting(isSubmitting) {
    const button = this.refs.submitButton;

    if (!button) {
      return;
    }

    button.disabled = isSubmitting;
    button.setAttribute('aria-busy', String(isSubmitting));
    button.textContent = isSubmitting ? this.#submittingLabel : this.#submitLabel;
  }
}

if (!customElements.get('netsuite-form')) {
  customElements.define('netsuite-form', NetsuiteForm);
}
